import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { translateRoomType } from '../../../constants/roomTypes';

const RoomTable = ({ rooms, onEdit, onDuplicate, onDelete, onViewDetails, selectedRooms, onSelectRoom, onSelectAll }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Disponível':
        return 'bg-success/10 text-success border-success/20';
      case 'Ocupado':
        return 'bg-error/10 text-error border-error/20';
      case 'Manutenção':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'Limpeza':
        return 'bg-secondary/10 text-secondary border-secondary/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const isAllSelected = rooms.length > 0 && selectedRooms.length === rooms.length;
  const isIndeterminate = selectedRooms.length > 0 && selectedRooms.length < rooms.length;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-10 md:w-12 px-2 md:px-4 py-2 md:py-3 text-left">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = isIndeterminate;
                  }}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  className="rounded border-border"
                />
              </th>
              <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-semibold text-foreground">Quarto</th>
              <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-semibold text-foreground">Tipo</th>
              <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-semibold text-foreground hidden lg:table-cell">Capacidade</th>
              <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-semibold text-foreground hidden lg:table-cell">Camas</th>
              <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-semibold text-foreground hidden xl:table-cell">Comodidades</th>
              <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-semibold text-foreground hidden lg:table-cell">Preço Solteiro</th>
              <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-semibold text-foreground">Preço</th>
              <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-semibold text-foreground">Status</th>
              <th className="px-2 md:px-4 py-2 md:py-3 text-center text-xs md:text-sm font-semibold text-foreground">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rooms.map((room) => (
              <tr
                key={room.id}
                className="hover:bg-muted/30 transition-smooth cursor-pointer"
                onClick={() => onViewDetails(room)}
              >
                <td className="px-2 md:px-4 py-2 md:py-3">
                  <input
                    type="checkbox"
                    checked={selectedRooms.includes(room.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      onSelectRoom(room.id, e.target.checked);
                    }}
                    className="rounded border-border"
                  />
                </td>
                <td className="px-2 md:px-4 py-2 md:py-3">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Bed" size={14} color="var(--color-primary)" />
                    </div>
                    <div>
                      <div className="text-xs md:text-sm font-medium text-foreground">{room.number}</div>
                      <div className="text-xs text-muted-foreground hidden md:block">Andar {room.floor}</div>
                    </div>
                  </div>
                </td>
                <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-foreground">{translateRoomType(room.type)}</td>
                <td className="px-2 md:px-4 py-2 md:py-3 hidden lg:table-cell">
                  <div className="flex items-center space-x-1 text-sm text-foreground">
                    <Icon name="Users" size={14} color="var(--color-muted-foreground)" />
                    <span>{room.capacity}</span>
                  </div>
                </td>
                <td className="px-2 md:px-4 py-2 md:py-3 text-sm text-foreground hidden lg:table-cell">{room.beds}</td>
                <td className="px-2 md:px-4 py-2 md:py-3 hidden xl:table-cell">
                  <div className="flex items-center space-x-2">
                    {room.hasAC && (
                      <div className="w-6 h-6 bg-primary/10 rounded flex items-center justify-center" title="Ar Condicionado">
                        <Icon name="Wind" size={12} color="var(--color-primary)" />
                      </div>
                    )}
                    {room.hasMinibar && (
                      <div className="w-6 h-6 bg-secondary/10 rounded flex items-center justify-center" title="Frigobar">
                        <Icon name="Coffee" size={12} color="var(--color-secondary)" />
                      </div>
                    )}
                    {room.hasBathroom && (
                      <div className="w-6 h-6 bg-accent/10 rounded flex items-center justify-center" title="Banheiro Privativo">
                        <Icon name="Bath" size={12} color="var(--color-accent)" />
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium text-foreground hidden lg:table-cell">
                  {formatPrice(room.singlePrice)}
                </td>
                <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium text-foreground">
                  {formatPrice(room.couplePrice)}
                </td>
                <td className="px-2 md:px-4 py-2 md:py-3">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(room.status)}`}>
                    {room.status}
                  </span>
                </td>
                <td className="px-2 md:px-4 py-2 md:py-3">
                  <div className="flex items-center justify-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(room);
                      }}
                      iconName="Edit"
                      className="p-2"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate(room);
                      }}
                      iconName="Copy"
                      className="p-2"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(room);
                      }}
                      iconName="Trash2"
                      className="p-2 text-error hover:text-error"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RoomTable;